import React, {useEffect, useState, useContext, useCallback, useRef} from "react";
import {Image} from "react-bootstrap";

import "./SideNav.css";
import {KeycloakContext} from "@context/KeycloakContext";
import {BRANDING_UPDATED_EVENT} from "@utils/branding";

export default function SideNavLogo({isExpanded}) {
    const {updateTokenAndFetch, user} = useContext(KeycloakContext);
    const [currLogo, setCurrLogo] = useState(null);
    const [currIcon, setCurrIcon] = useState(null);
    const [logoOpt, setLogoOpt] = useState(null);
    const [iconOpt, setIconOpt] = useState(null);
    const [orgName, setOrgName] = useState("");
    const [logoSettingLoading, setLogoSettingLoading] = useState(true);
    const [brandingVersion, setBrandingVersion] = useState(0);

    const getLogoIcon = useCallback(async (fileId, type) => {
        try {
            const response = await updateTokenAndFetch(`/files/${fileId}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
                cache: "force-cache"
            });
            if (response.ok) {
                const blob = await response.blob();
                const imageUrl = URL.createObjectURL(blob);
                if (type === "logo") {
                    setCurrLogo(prevLogo => {
                        // Revoke previous URL if it exists
                        if (prevLogo) URL.revokeObjectURL(prevLogo);
                        return imageUrl;
                    });
                } else if (type === "icon") {
                    setCurrIcon(prevIcon => {
                        // Revoke previous URL if it exists
                        if (prevIcon) URL.revokeObjectURL(prevIcon);
                        return imageUrl;
                    });
                }
            }
        } catch (err) {
            console.error(`❌ Error fetching your profile picture: ${err}`);
        } finally {
            // setPicLoading(false);
        }
    }, [updateTokenAndFetch])

    const logoIdRef = useRef(null);
    const iconIdRef = useRef(null);


    useEffect(() => {
        // Prevent unnecessary API calls if component is unmounting
        let isMounted = true;

        const getOrg = async () => {
            try {
                const res = await updateTokenAndFetch(
                    `/users/me/org/branding`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                        },
                    }
                );
                if (res.ok && isMounted) {
                    const data = await res.json();
                    const organization = data.organization;
                    setLogoOpt(organization.branding_expanded);
                    setIconOpt(organization.branding_collapsed);
                    setOrgName(organization.name ?? "");

                    // Only fetch if the IDs have changed
                    const logoRefId = organization.logo?.id ?? null;
                    const iconRefId = organization.icon?.id ?? null;

                    if (logoRefId && logoRefId !== logoIdRef.current) {
                        logoIdRef.current = logoRefId;
                        await getLogoIcon(logoRefId, "logo");
                    } else if (!logoRefId) {
                        logoIdRef.current = null;
                        setCurrLogo(prevLogo => {
                            if (prevLogo) URL.revokeObjectURL(prevLogo);
                            return null;
                        });
                    }

                    if (iconRefId && iconRefId !== iconIdRef.current) {
                        iconIdRef.current = iconRefId;
                        await getLogoIcon(iconRefId, "icon");
                    } else if (!iconRefId) {
                        iconIdRef.current = null;
                        setCurrIcon(prevIcon => {
                            if (prevIcon) URL.revokeObjectURL(prevIcon);
                            return null;
                        });
                    }
                }
            } catch (err) {
                if (isMounted) {
                    console.error("Error retrieving organization data:", err);
                    // setToastMessage(`❌ Error retrieving organization data: ${err}`);
                    // setShowToast(true);
                }
            } finally {
                if (isMounted) {
                    setLogoSettingLoading(false);
                }
            }
        };

        getOrg();

        // Cleanup function
        return () => {
            isMounted = false;
        };
    }, [getLogoIcon, setLogoOpt, setIconOpt, updateTokenAndFetch, brandingVersion]);

    useEffect(() => {
        const handleBrandingUpdate = () => setBrandingVersion((prev) => prev + 1);
        window.addEventListener(BRANDING_UPDATED_EVENT, handleBrandingUpdate);
        return () => window.removeEventListener(BRANDING_UPDATED_EVENT, handleBrandingUpdate);
    }, []);


    const currLogoRef = useRef(null);
    const currIconRef = useRef(null);
    currLogoRef.current = currLogo;
    currIconRef.current = currIcon;

    useEffect(() => {
        return () => {
            if (currLogoRef.current) URL.revokeObjectURL(currLogoRef.current);
            if (currIconRef.current) URL.revokeObjectURL(currIconRef.current);
        };
    }, []);


    const brandedOpt = logoOpt === "Co-Branding" || logoOpt === "White Label";
    const partnerName = orgName || user?.partner?.name || user?.customer?.name || "";
    const brandText = brandedOpt && partnerName ? partnerName : "NetCyberOps";

    return (
        <>
            {!isExpanded && iconOpt === "Co-Branding" && (
                <>
                    <div className="d-flex co-brand-nco-icon-container">
                        <div className="side-nav-icon-container">
                            <Image src="/nco.svg" className="side-nav-icon nco-logo-icon"/>
                        </div>
                    </div>
                    <hr id="co-brand-hr"/>
                </>
            )}
            <div className={`d-flex nav-desktop-container`}>
                {!currIcon ? (
                    <div
                        className={`side-nav-icon-container ${
                            logoSettingLoading ? "no-bgc" : ""
                        }`}
                    >
                        {!logoSettingLoading && (
                            <Image src="/nco.svg" className="side-nav-icon nco-logo-icon"/>
                        )}
                    </div>
                ) : (!isExpanded && (iconOpt === "Co-Branding" || iconOpt === "White Label")) ||
                (isExpanded && logoOpt === "White Label") ? (
                    <>
                        {currIcon ? (
                            <div className="side-nav-custom-icon-container">
                                <Image src={currIcon} className="side-nav-custom-icon"/>
                            </div>
                        ) : (
                            <div className="side-nav-icon-container">
                                <Image src="/nco.svg" className="side-nav-icon nco-logo-icon"/>
                            </div>
                        )}
                    </>
                ) : isExpanded && logoOpt === "Co-Branding" ? (
                    <div className="side-nav-icon-container">
                        <Image src="/nco.svg" className="side-nav-icon nco-logo-icon"/>
                    </div>
                ) : (
                    <div
                        className={`side-nav-icon-container ${
                            logoSettingLoading ? "no-bgc" : ""
                        }`}
                    >
                        {!logoSettingLoading && (
                            <Image src="/nco.svg" className="side-nav-icon nco-logo-icon"/>
                        )}
                    </div>
                )}
                <div id="logo-verticle-hr"></div>
                {!currLogo ? (
                    <div className="sidenav-heading">{brandText}</div>
                ) : logoOpt === "White Label" ? (
                    <div className="sidenav-heading">
                        <Image
                            src={currLogo}
                            alt="Company Logo"
                            id="white-label-logo"
                            fluid
                        />
                    </div>
                ) : logoOpt === "Co-Branding" ? (
                    <div className="sidenav-heading d-flex">
                        <Image src={currLogo} alt="Company Logo" id="co-brand-logo" fluid/>
                    </div>
                ) : (
                    <div className="sidenav-heading">NetCyberOps</div>
                )}
            </div>
        </>
    );
}
